<template>
  <div class="map-picker-modal" v-if="visible">
    <div class="modal-content">
      <div class="header">
        <h3>选择经纬度</h3>
        <span class="close-btn" @click="handleCancel">×</span>
      </div>

      <div class="search-bar">
        <input
          v-model="keyword"
          placeholder="搜索地点"
          @keyup.enter="handleSearch"
        />
      </div>

      <div ref="mapContainer" class="map-container"></div>

      <div class="footer">
        <button class="cancel-btn" @click="handleCancel">取消</button>
        <button class="confirm-btn" @click="handleConfirm">确定</button>
      </div>
    </div>
  </div>
</template>

<script>
const TIANDITU_KEY = '30e0eb7ec9b2e6bcd1b4c398a7ba5710'
const SCRIPT_ID = 'tianditu-script-loader'
export default {
  name: 'MapLocationPicker',
  props: {
    visible: { type: Boolean, default: false },
    // 传入默认坐标：CGCS2000（字符串/数字都支持）
    defaultLng: { type: [Number, String], default: null },
    defaultLat: { type: [Number, String], default: null }
  },
  data() {
    return {
      map: null,
      marker: null,
      keyword: '',
      // 强制默认北京坐标，防止定位失败跑到非洲
      selectedLng: 116.403874,
      selectedLat: 39.914885
    }
  },

  watch: {
    visible(val) {
      if (val) {
        this.initEntry()
      } else {
        this.destroyMap()
      }
    }
  },
  mounted() {
    this.initEntry()
  },
  methods: {
    // 弹窗打开统一入口
    async initEntry() {
      // 每次打开强制重置为默认北京，防止上次异常状态残留
      this.selectedLng = 116.403874
      this.selectedLat = 39.914885
      this.keyword = ''
      await this.$nextTick()
      await this.loadScript()
      this.initMap()
    },

    // 销毁地图实例
    destroyMap() {
      this.map = null
      this.marker = null
    },

    // 加载天地图API
    loadScript() {
      return new Promise((resolve) => {
        if (window.T) {
          resolve()
          return
        }
        let script = document.getElementById(SCRIPT_ID)
        if (script) {
          script.onload = resolve
          return
        }
        script = document.createElement('script')
        script.id = SCRIPT_ID
        script.src = `https://api.tianditu.gov.cn/api?v=4.0&tk=${TIANDITU_KEY}`
        script.onload = resolve
        document.body.appendChild(script)
      })
    },

    // JSONP 逆地理编码 防跨域 + 自动清理
    getAddressByLngLat(lng, lat) {
      return new Promise((resolve) => {
        const callbackName = 'tdt_' + Date.now() + Math.random().toString(36).slice(2, 8)
        const ds = JSON.stringify({ lonlat: `${lng},${lat}` })
        const url = `https://api.tianditu.gov.cn/geocoder?type=geocode&tk=${TIANDITU_KEY}&ds=${encodeURIComponent(ds)}&callback=${callbackName}`

        const script = document.createElement('script')
        script.src = url

        const timer = setTimeout(() => {
          cleanUp()
          resolve({ formattedAddress: '请求超时' })
        }, 8000)

        const cleanUp = () => {
          clearTimeout(timer)
          if (window[callbackName]) delete window[callbackName]
          if (script.parentNode) script.parentNode.removeChild(script)
        }

        window[callbackName] = (data) => {
          cleanUp()
          if (data?.status === '0' && data.result) {
            const r = data.result
            resolve({
              province: r.province || '',
              city: r.city || '',
              district: r.district || '',
              address: r.address || '',
              formattedAddress: `${r.province || ''}${r.city || ''}${r.district || ''}${r.address || ''}`
            })
          } else {
            resolve({ formattedAddress: '获取地址失败' })
          }
        }

        script.onerror = () => {
          cleanUp()
          resolve({ formattedAddress: '网络异常' })
        }

        document.body.appendChild(script)
      })
    },

    // 初始化地图（核心逻辑：优先使用传入坐标）
    async initMap() {
      const T = window.T
      if (!T || !this.$refs.mapContainer) return

      this.$refs.mapContainer.innerHTML = ''

      // 1. 优先使用父组件传入的 CGCS2000 坐标
      const inputLng = Number(this.defaultLng)
      const inputLat = Number(this.defaultLat)

      if (!isNaN(inputLng) && !isNaN(inputLat) && inputLng !== 0 && inputLat !== 0) {
        this.selectedLng = inputLng
        this.selectedLat = inputLat
        console.error('使用默认选中坐标', inputLng, inputLat)
      } else {
        // 2. 没有传入坐标，才尝试浏览器定位（仅在https环境下有效）
        try {
          const loc = await this.getCurrentLocation()
          if (loc) {
            // console.error("使用定位坐标",loc.lng,loc.lat);
            this.selectedLng = loc.lng
            this.selectedLat = loc.lat
          }
        } catch (e) {
          console.warn('定位失败，使用默认北京坐标')
        }
      }

      // 3. 天地图 CGCS2000 直接赋值，无需任何坐标转换
      this.map = new T.Map(this.$refs.mapContainer)
      const centerPoint = new T.LngLat(this.selectedLng, this.selectedLat)
      this.map.centerAndZoom(centerPoint, 14)
      this.map.enableScrollWheelZoom(true)

      this.addMarker(this.selectedLng, this.selectedLat)

      // 点击选点
      this.map.addEventListener('click', (e) => {
        this.selectedLng = e.lnglat.lng
        this.selectedLat = e.lnglat.lat
        this.addMarker(this.selectedLng, this.selectedLat)
      })
    },
    getCurrentLocation() {
      return new Promise((resolve, reject) => {
        // 初始化定位
        const geoloc = new T.Geolocation()
        geoloc.enableHighAccuracy()
        geoloc.getCurrentPosition(
          function(pos) {
            const { lnglat } = pos
            console.error('======getCurrentPosition===success======', lnglat)
            resolve(lnglat)
          },
          function(err) {
            // 失败：用户拒绝/无GPS/非HTTPS
            console.error('定位失败：', err)
            alert('定位失败，请允许定位或切换HTTPS环境')
            reject(err)
          },
          { timeout: 10000 } // 超时10秒
        )
      })
    },
    addMarker(lng, lat) {
      const T = window.T
      if (this.marker) {
        try {
          this.map.removeOverLay(this.marker)
        } catch (e) {
        }
      }
      this.marker = new T.Marker(new T.LngLat(lng, lat))
      this.map.addOverLay(this.marker)
      this.map.panTo(new T.LngLat(lng, lat))
    },

    handleSearch() {
      if (!this.keyword.trim()) return
      const T = window.T
      const geocoder = new T.Geocoder()
      geocoder.getPoint(this.keyword, (res) => {
        if (res.getStatus() === 0) {
          const p = res.getLocationPoint()
          this.selectedLng = p.lng
          this.selectedLat = p.lat
          this.addMarker(p.lng, p.lat)
        }
      })
    },

    handleCancel() {
      this.$emit('update:visible', false)
    },

    async handleConfirm() {
      const addr = await this.getAddressByLngLat(this.selectedLng, this.selectedLat)
      this.$emit('confirm', {
        lng: this.selectedLng,
        lat: this.selectedLat,
        ...addr
      })
      this.$emit('update:visible', false)
    }
  },

  beforeDestroy() {
    this.destroyMap()
  }
}
</script>

<style scoped>
.map-picker-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background: #fff;
  width: 800px;
  height: 600px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.header {
  padding: 14px 16px;
  border-bottom: 1px solid #eee;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.close-btn {
  font-size: 22px;
  cursor: pointer;
}

.search-bar {
  padding: 10px 16px;
}

.search-bar input {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.map-container {
  flex: 1;
  width: 100%;
  height: 100%;
}

.footer {
  padding: 12px 16px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.cancel-btn {
  padding: 6px 14px;
  border: 1px solid #ddd;
  background: #fff;
  border-radius: 4px;
  cursor: pointer;
}

.confirm-btn {
  padding: 6px 14px;
  background: #ffb900;
  color: #fff;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
</style>